// This optional code is used to register a service worker.
// register() is not called by default.

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

/**
 * How often an open tab asks the browser whether a new service worker exists.
 *
 * The browser re-fetches the service worker script on page load and on real
 * navigations. This app routes on the client, so neither happens while a tab
 * stays open, and `onupdatefound` below cannot fire without being asked.
 */
export const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

let updateCheckTimer: ReturnType<typeof setInterval> | undefined;
let visibilityListener: (() => void) | undefined;

/**
 * Begin asking for updates: on a timer, and whenever the tab regains focus
 * (which covers a tab left open overnight far sooner than the timer would).
 *
 * This only ASKS. A found update flows through the existing onupdatefound →
 * onUpdate path, which raises a toast and then waits for the user; `check`
 * below declines to ask at all in the one state where a found update could
 * take the tab over without being asked for.
 */
export function startUpdateChecks(registration: ServiceWorkerRegistration): void {
  stopUpdateChecks();

  const check = () => {
    // Only ask while a service worker controls this tab.
    //
    // On an uncontrolled tab — the state a hard reload leaves behind — asking
    // buys nothing: registerValidSW below routes an install found with no
    // controller to config.onSuccess, not to the config.onUpdate branch that
    // raises the update toast this check exists to trigger. And it costs a
    // lot: a worker that activates while it controls no client can claim this
    // tab outright, firing controllerchange with nobody having clicked
    // anything. These checks run mid-session, on a page that may be holding
    // input nobody has saved, so this one does not create that opening.
    if (!navigator.serviceWorker.controller) return;

    // A rejected check means offline or a transient failure. The next check
    // retries, so swallowing it keeps the loop alive rather than ending it.
    registration.update().catch(() => undefined);
  };

  updateCheckTimer = setInterval(check, UPDATE_CHECK_INTERVAL_MS);
  visibilityListener = () => {
    if (document.visibilityState === 'visible') check();
  };
  document.addEventListener('visibilitychange', visibilityListener);
}

export function stopUpdateChecks(): void {
  if (updateCheckTimer !== undefined) {
    clearInterval(updateCheckTimer);
    updateCheckTimer = undefined;
  }
  if (visibilityListener !== undefined) {
    document.removeEventListener('visibilitychange', visibilityListener);
    visibilityListener = undefined;
  }
}

export function register(config?: Config) {
  if ((process.env.NODE_ENV === 'production' || (process.env.NODE_ENV as string) === 'staging') && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log('This web app is being served cache-first by a service worker.');
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              console.log('New content is available and will be used when all tabs for this page are closed.');
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              console.log('Content is cached for offline use.');
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };

      startUpdateChecks(registration);
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (response.status === 404 || (contentType != null && contentType.indexOf('javascript') === -1)) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
